// Template → document (M14), and the two edits every ad needs: apply the brand, set the ad's fields. Both
// rewrite the affected nodes (resolved text, brand fonts, touched appearances) so the display list redraws
// them; colours follow the brand through the global swatches 'brand-<role>'.
import type { AdData, BrandData, Doc, ImageNode, Node, TextNode } from '../model/types';
import { boundText, SAMPLE_AD } from '../ad/fields';
import { nearestWeight } from '../text/fontCatalog';
import { Builder, BRAND_ROLES, type TemplateDef } from './dsl';
import { TEMPLATES } from './library';
import { cmd } from '../commands/commands';
import type { Command } from '../commands/history';

export const templateById = (id: string) => TEMPLATES.find((t) => t.id === id);

export function walk(doc: Doc, fn: (n: Node) => void) {
  const visit = (n: Node) => {
    fn(n);
    if (n.kind === 'group') n.children.forEach(visit);
  };
  for (const l of doc.layers) l.children.forEach(visit);
}
export const textNodes = (doc: Doc) => {
  const out: TextNode[] = [];
  walk(doc, (n) => n.kind === 'text' && out.push(n));
  return out;
};
export const imageNodes = (doc: Doc, role?: 'photo' | 'logo') => {
  const out: ImageNode[] = [];
  walk(doc, (n) => n.kind === 'image' && (!role || n.role === role) && out.push(n));
  return out;
};

const mergeAd = (base: AdData, p?: Partial<AdData>): AdData => ({
  ...base,
  ...p,
  items: p?.items ?? base.items,
});

/** Applies brand + ad to a (draft) document in place. */
export function applyBrandAndAd(d: Doc, brand: BrandData, ad: AdData) {
  d.brand = brand;
  d.ad = ad;
  for (const r of BRAND_ROLES) {
    const sw = d.swatches.find((s) => s.id === `brand-${r}`);
    if (sw) sw.color = { rgb: [...brand.colors[r]] as [number, number, number], alpha: 1 };
  }
  walk(d, (n) => {
    // A new appearance object makes every node redraw with the new swatch colours.
    n.appearance = { ...n.appearance };
    if (n.kind === 'text') {
      if (n.style.fontRole) {
        const want = n.style.fontRole === 'heading' ? brand.headingFont : brand.bodyFont;
        const f = nearestWeight(want, n.style.weight);
        n.style = { ...n.style, family: f.family, weight: f.weight };
      }
      n.text = boundText(n, ad, brand);
    }
    if (n.kind === 'image' && n.role === 'logo') {
      n.assetId = brand.logoAsset;
      n.iw = brand.logoW;
      n.ih = brand.logoH;
    }
  });
  if (d.artboards[0]?.background) d.artboards[0] = { ...d.artboards[0] };
}

/** A new document from a template, with the brand and the template's sample content. */
export function instantiate(t: TemplateDef, brand: BrandData, ad?: AdData): Doc {
  const b = new Builder(t.format);
  t.build(b);
  const doc = b.doc(t.name.es);
  doc.template = t.id;
  applyBrandAndAd(doc, brand, mergeAd(ad ?? SAMPLE_AD, ad ? undefined : t.ad));
  return doc;
}

/** Command: apply a (new) brand to the open ad. One undo step. */
export const applyBrand = (brand: BrandData): Command =>
  cmd('cmdApplyBrand', (d) => applyBrandAndAd(d, brand, d.ad ?? SAMPLE_AD));

/** Command: change the ad's fields (typing in the form merges into one undo step per field). */
export const setAd = (fn: (a: AdData) => void, merge?: string): Command =>
  cmd(
    'cmdEditAd',
    (d) => {
      if (!d.ad) d.ad = structuredClone(SAMPLE_AD);
      fn(d.ad);
      walk(d, (n) => {
        if (n.kind === 'text' && n.bind && n.bind.role !== 'free') {
          const t = boundText(n, d.ad, d.brand);
          if (t !== n.text) n.text = t;
        }
      });
    },
    merge,
  );

/** Command: put an image into a slot (photo or logo). */
export const fillSlot = (
  id: string,
  asset: { id: string; w: number; h: number },
  photoKey?: string,
): Command =>
  cmd('cmdFillPhoto', (d) =>
    walk(d, (n) => {
      if (n.id === id && n.kind === 'image') {
        n.assetId = asset.id;
        n.iw = asset.w;
        n.ih = asset.h;
        n.zoom = 1;
        n.offsetX = 0;
        n.offsetY = 0;
        n.photoKey = photoKey;
      }
    }),
  );
